import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export default function Login() {
  const { login, error, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@shopmerse.com");
  const [password, setPassword] = useState("Admin@123");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (user && user !== false) nav("/", { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) nav("/", { replace: true });
  };

  return (
    <div className="min-h-screen w-full grid md:grid-cols-2 bg-slate-50">
      {/* Left panel (brand) */}
      <div className="hidden md:flex flex-col justify-between bg-slate-900 text-white p-10 relative overflow-hidden">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-amber-600 flex items-center justify-center">
            <Sparkles className="w-5 h-5" strokeWidth={2} />
          </div>
          <div>
            <div className="font-heading font-bold tracking-tight text-lg leading-none">Shopmerse</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mt-1">Lead Intelligence CRM</div>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <div className="text-[11px] uppercase tracking-[0.24em] text-amber-500 font-semibold mb-4">Africa · GCC · Labs · Industry</div>
          <h2 className="font-heading text-4xl font-bold leading-tight tracking-tight text-slate-50">
            Every lead. Every source. <span className="text-amber-500">Every decision — logged.</span>
          </h2>
          <p className="text-slate-400 mt-5 leading-relaxed text-[15px]">
            A 12-criterion weighted scoring engine that will not let a claim be saved without its source. Built for laboratory equipment, industrial chemicals, and food-processing pipelines across primary and secondary markets.
          </p>
        </div>

        <div className="grid grid-cols-3 gap-6 relative z-10 text-slate-300">
          <div>
            <div className="font-heading font-bold text-3xl text-white">12</div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mt-1">Scored criteria</div>
          </div>
          <div>
            <div className="font-heading font-bold text-3xl text-amber-500">V1—V5</div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mt-1">Data rules</div>
          </div>
          <div>
            <div className="font-heading font-bold text-3xl text-white">17</div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 mt-1">Target markets</div>
          </div>
        </div>

        {/* decorative gold ring */}
        <div className="absolute -right-40 -bottom-40 w-96 h-96 rounded-full border border-amber-600/20"></div>
        <div className="absolute -right-20 -bottom-20 w-96 h-96 rounded-full border border-amber-600/10"></div>
      </div>

      {/* Right panel (form) */}
      <div className="flex items-center justify-center p-6 sm:p-10">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-amber-700 font-semibold">Sign in</div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 mt-2 tracking-tight">Welcome back.</h1>
            <p className="text-sm text-slate-500 mt-2">Use your Shopmerse credentials to continue.</p>
          </div>

          {error && (
            <div className="p-3 rounded-md bg-rose-50 border border-rose-200 text-sm text-rose-700" data-testid="login-error">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email"
                className="mt-1.5 h-11 border-slate-300 focus-visible:ring-amber-500"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password"
                className="mt-1.5 h-11 border-slate-300 focus-visible:ring-amber-500"
                required
              />
            </div>
          </div>

          <Button type="submit" disabled={busy} data-testid="login-submit"
                  className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium">
            {busy ? "Signing in…" : (<>Sign in <ArrowRight className="w-4 h-4 ml-2" /></>)}
          </Button>

          <div className="text-xs text-slate-400 text-center pt-4 border-t border-slate-100">
            Admin: <span className="font-mono text-slate-600">admin@shopmerse.com</span> · Researcher: <span className="font-mono text-slate-600">vaishnav@shopmerse.com</span>
          </div>
        </form>
      </div>
    </div>
  );
}
